import { useEffect, useState } from 'react'

import { Alert, Button, Field, Input, SurfaceCard } from '@ministries/storybook'
import { Form } from 'react-router'

import type { ApiMode, User } from '@app/api'
import { useI18n } from '@app/utilities/i18n/provider'

export type LoginActionData = {
  email: string
  message: string
  status: 'error' | 'success'
}

interface LoginProps {
  accounts: User[]
  actionData?: LoginActionData
  apiMode: ApiMode
  defaultEmail: string
  isLoading: boolean
  isSubmitting: boolean
}

function resolveInitialEmail(
  defaultEmail: string,
  actionData?: LoginActionData
): string {
  return actionData?.email ?? defaultEmail
}

export default function LoginScreen({
  accounts,
  actionData,
  apiMode,
  defaultEmail,
  isLoading,
  isSubmitting
}: LoginProps) {
  const { t } = useI18n()
  const [selectedEmail, setSelectedEmail] = useState(
    resolveInitialEmail(defaultEmail, actionData)
  )

  useEffect(() => {
    setSelectedEmail(resolveInitialEmail(defaultEmail, actionData))
  }, [actionData?.email, defaultEmail])

  if (isLoading) {
    return (
      <main className="min-h-screen bg-zinc-100 px-4 py-8 text-zinc-900">
        <SurfaceCard className="mx-auto w-full max-w-sm rounded-xl p-5">
          <h1 className="text-2xl font-semibold">{t('auth.title')}</h1>
          <p className="mt-1 text-sm text-zinc-600">{t('auth.loading')}</p>
        </SurfaceCard>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-zinc-100 px-4 py-8 text-zinc-900">
      <SurfaceCard className="mx-auto w-full max-w-sm rounded-xl p-5">
        <h1 className="text-2xl font-semibold">{t('auth.title')}</h1>
        <p className="mt-1 text-sm text-zinc-600">
          {apiMode === 'real'
            ? t('auth.description.real')
            : t('auth.description.mock')}
        </p>

        <Form className="mt-5 space-y-3" method="post">
          <Field htmlFor="email" label={t('auth.email.label')}>
            <Input
              id="email"
              type="email"
              name="email"
              required={true}
              autoComplete="email"
              value={selectedEmail}
              onChange={event => setSelectedEmail(event.target.value)}
              placeholder={t('auth.email.placeholder')}
            />
          </Field>

          <Field htmlFor="password" label={t('auth.password.label')}>
            <Input
              id="password"
              type="password"
              name="password"
              required={true}
              autoComplete="current-password"
              placeholder={t('auth.password.placeholder')}
            />
          </Field>

          <Button
            type="submit"
            primary={true}
            disabled={isSubmitting}
            className="h-11 w-full"
          >
            {isSubmitting ? t('auth.submitting') : t('auth.submit')}
          </Button>
        </Form>

        {actionData?.status === 'error' ? (
          <Alert className="mt-3" tone="danger">
            {t(actionData.message)}
          </Alert>
        ) : null}

        {actionData?.status === 'success' ? (
          <Alert className="mt-3" tone="success">
            {actionData.message}
          </Alert>
        ) : null}

        {accounts.length > 0 ? (
          <ul className="mt-4 space-y-2 rounded-md border border-zinc-200 bg-zinc-50 p-3">
            {accounts.slice(0, 3).map(account => (
              <li
                key={account.id}
                className="flex items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {account.displayName}
                  </p>
                  <p className="truncate text-xs text-zinc-500">
                    {account.email}
                  </p>
                </div>
                <Button
                  type="button"
                  size="small"
                  variant="secondary"
                  onClick={() => setSelectedEmail(account.email)}
                >
                  {t('auth.mock.use')}
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <Alert className="mt-4">
            {apiMode === 'real' ? t('auth.real.hint') : t('auth.mock.hint')}
          </Alert>
        )}
      </SurfaceCard>
    </main>
  )
}
